import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { parseApiError, useAuth } from '@saas-kit/lib';
import { Button, toast } from '@saas-kit/ui';
import {
  DataTable,
  type BulkAction,
  type PaginationState,
} from '@saas-kit/ui/DataTable';

import {
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  type NotificationRecord,
} from './api';
import { buildInboxColumns } from './columns';

const DEFAULT_PAGE_SIZE = 25;

function isMarkable(row: NotificationRecord): boolean {
  return row.channel === 'in_app' && row.status === 'sent';
}

export function InboxTab(): JSX.Element {
  const { t } = useTranslation('notifications');
  const selectedTenant = useAuth((s) => s.selectedTenant);
  const queryClient = useQueryClient();

  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [markPendingId, setMarkPendingId] = useState<string | null>(null);

  const status = filters.status ?? '';
  const channel = filters.channel ?? '';
  const kind = filters.kind ?? '';

  const query = useQuery({
    queryKey: [
      'tenant-notifications',
      selectedTenant?.tenant_id,
      { offset, limit: pageSize, status, channel, kind },
    ],
    queryFn: () =>
      listNotifications({
        offset,
        limit: pageSize,
        ...(status ? { status } : {}),
        ...(channel ? { channel } : {}),
        ...(kind ? { kind } : {}),
      }),
    enabled: Boolean(selectedTenant?.tenant_id),
    placeholderData: (prev) => prev,
  });

  async function refresh(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['tenant-notifications'] });
    // Keeps the shell bell + drawer (queryKey prefix ['notifications']) in sync.
    await queryClient.invalidateQueries({ queryKey: ['notifications'] });
  }

  const markReadMutation = useMutation({
    mutationFn: async (row: NotificationRecord) => {
      setMarkPendingId(row.id);
      try {
        return await markNotificationRead(row.id);
      } finally {
        setMarkPendingId(null);
      }
    },
    onSuccess: refresh,
    onError: (e) => toast.error(parseApiError(e).message),
  });

  const markAllMutation = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: async (count) => {
      await refresh();
      toast.success(t('inbox.toasts.marked_all', { count }));
    },
    onError: (e) => toast.error(parseApiError(e).message),
  });

  const columns = useMemo(
    () =>
      buildInboxColumns(t, {
        onMarkRead: (row) => markReadMutation.mutate(row),
        markPendingId,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, markPendingId],
  );

  const bulkActions = useMemo<BulkAction<NotificationRecord>[]>(
    () => [
      {
        id: 'mark-read',
        label: t('inbox.actions.mark_read'),
        variant: 'secondary',
        isDisabled: (rows) =>
          rows.some(isMarkable) ? null : t('inbox.bulk.none_markable'),
        onRun: async (rows) => {
          const markable = rows.filter(isMarkable);
          if (markable.length === 0) return;
          try {
            await Promise.all(markable.map((r) => markNotificationRead(r.id)));
            await refresh();
            toast.success(t('inbox.toasts.marked', { count: markable.length }));
          } catch (e) {
            toast.error(parseApiError(e).message);
          }
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t],
  );

  const items = query.data?.items ?? [];
  const page = query.data?.page;

  const pagination: PaginationState = {
    pageIndex: Math.floor(offset / pageSize),
    pageSize,
    hasNext: page ? page.offset + page.limit < page.total : false,
    hasPrev: offset > 0,
    onNext: () => setOffset(offset + pageSize),
    onPrev: () => setOffset(Math.max(0, offset - pageSize)),
    onPageSizeChange: (n) => {
      setPageSize(n);
      setOffset(0);
    },
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button
          variant="secondary"
          disabled={markAllMutation.isPending}
          onClick={() => markAllMutation.mutate()}
        >
          {markAllMutation.isPending
            ? t('inbox.actions.marking_all')
            : t('inbox.actions.mark_all')}
        </Button>
      </div>
      <DataTable<NotificationRecord>
        data={items}
        columns={columns}
        rowKey={(row) => row.id}
        testId="tenant-notifications"
        selectable
        bulkActions={bulkActions}
        filters={filters}
        onFilterChange={(id, value) => {
          setFilters((cur) => ({ ...cur, [id]: value }));
          setOffset(0);
        }}
        pagination={pagination}
        isLoading={query.isLoading}
        error={query.isError ? t('errors.load_failed') : null}
        emptyText={t('inbox.empty')}
        exportable
        exportFilename="notifications"
      />
    </div>
  );
}
